import { ReactElement, useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  List,
  Collapse,
  Link,
  Box,
  Stack,
  ListItemIcon,
  ListItemButtonProps,
  styled,
  ListItemButton,
  ListItemText,
  Drawer,
} from '@mui/material';

import { ThemeContext } from 'src/contexts/ThemeContext';
import { AppButton, StackRow } from 'src/components';
import { IconChevronDown, IconChevronRight, IconLock } from 'src/libraries/icons';
import RoleBasedGuard from 'src/guards/RoleBasedGuard';
import { getNavConfig } from 'src/layouts/dashboard/navbar/NavConfig';
import CollapseButton from 'src/layouts/dashboard/navbar/CollapseButton';
import Logo from 'src/components/Logo';
import Scrollbar from 'src/components/Scrollbar';
import useMerchant from 'src/hooks/useMerchant';
import useResponsive from 'src/hooks/useResponsive';
import useCollapseDrawer from 'src/hooks/useCollapseDrawer';
import { NavItemProps } from 'src/layouts/dashboard/navbar/types';
import { getActive, isExternalLink } from './utils';
import useSubscriptions from 'src/hooks/useSubscriptions';

//
//

export type SidebarItemProps = {
  title: string;
  path: string;
  code?: string;
  icon?: ReactElement;
  disabled?: boolean;
  roles?: string[];
  children?: any;
  subtitle?: string;
  featureCode?: string;
  id?: string;
  hide?: boolean;
  isDynamicPath?: boolean;
  defaultPath?: string;
};

export const Sidebar: React.FC<{ isOpenSidebar: boolean; onCloseSidebar: VoidFunction }> = ({
  isOpenSidebar,
  onCloseSidebar,
}) => {
  const { merchant } = useMerchant();
  const { features } = useSubscriptions();
  const { pathname } = useLocation();

  const isDesktop = useResponsive('up', 'lg');

  const { isCollapse, collapseClick, onToggleCollapse } = useCollapseDrawer();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: isCollapse ? 'initial' : 'space-between',
          gap: 1,
          '&:before, &:after': { content: 'none' },
        },
      }}
    >
      <Stack
        spacing={3}
        sx={{ py: 2, px: 2.5, flexShrink: 0, ...(isCollapse && { alignItems: 'center' }) }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pl: 2 }}>
          {isCollapse ? (
            <CollapseButton
              onToggleCollapse={onToggleCollapse}
              collapseClick={collapseClick}
              sx={{ py: 0 }}
            />
          ) : (
            <Logo />
          )}

          {isDesktop && !isCollapse && (
            <CollapseButton onToggleCollapse={onToggleCollapse} collapseClick={collapseClick} />
          )}
        </Stack>

        <SidebarContent navConfig={getNavConfig(merchant?.programmes, features)} isCollapse={isCollapse} />
      </Stack>
    </Scrollbar>
  );

  return (
    <>
      {!isDesktop && (
        <Drawer open={isOpenSidebar} onClose={onCloseSidebar} PaperProps={{ sx: { width: 280 } }}>
          {renderContent}
        </Drawer>
      )}
    </>
  );
};

const SidebarContent: React.FC<{
  isCollapse?: boolean;
  navConfig: SidebarItemProps[];
}> = ({ navConfig, isCollapse, ...other }) => {
  return (
    <Box {...other}>
      {navConfig.map((navItem, idx) => (
        <List key={idx} disablePadding>
          <NavList
            key={navItem.title + navItem.path}
            data={navItem}
            depth={1}
            hasChildren={!!navItem.children}
            isCollapse={isCollapse}
          />
        </List>
      ))}
    </Box>
  );
};

//

type NavListRootProps = {
  data: SidebarItemProps;
  depth: number;
  hasChildren: boolean;
  isCollapse?: boolean;
};

const NavList = ({ data, depth, hasChildren, isCollapse = false }: NavListRootProps) => {
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { themeHasBeenChanged, setThemeHasBeenChanged } = useContext(ThemeContext);
  const { pathname } = useLocation();

  const active = getActive(data.path, pathname);

  const [open, setOpen] = useState(active);

  const onClickCloseSnackbar = () => {
    closeSnackbar('snackbar_theme_unsaved_changes');
    setThemeHasBeenChanged(false);
    navigate(data.path);
  };

  const handleClickItem = () => {
    if (!hasChildren) {
      if (themeHasBeenChanged) {
        enqueueSnackbar('There are unsaved changes!', {
          key: 'snackbar_theme_unsaved_changes',
          autoHideDuration: 7000,
          variant: 'warning',
          action: (
            <AppButton onClick={onClickCloseSnackbar} variant="outlined" color="secondary">
              Leave anyway
            </AppButton>
          ),
        });
      } else {
        navigate(data?.defaultPath || data.path);
      }
    }
    setOpen(!open);
  };

  return (
    <>
      {isExternalLink(data.path) ? (
        <Link href={data.path} target="_blank" rel="noopener" underline="none">
          <NavItem item={data} depth={depth} open={open} active={active} isCollapse={isCollapse} />
        </Link>
      ) : (
        <NavItem
          item={data}
          depth={depth}
          open={open}
          active={active}
          isCollapse={isCollapse}
          onClick={handleClickItem}
        />
      )}

      {!isCollapse && hasChildren && (
        <Collapse in={open} unmountOnExit>
          <List component="div" disablePadding>
            <NavSubList data={data.children} depth={depth} />
          </List>
        </Collapse>
      )}
    </>
  );
};

//

const NavSubList: React.FC<{ data: SidebarItemProps[]; depth: number }> = ({ data, depth }) => {
  return (
    <>
      {data
        .filter((el) => !el.hide)
        .map((list) => (
          <NavList
            key={list.title + list.path}
            data={list}
            depth={depth + 1}
            hasChildren={!!list.children}
          />
        ))}
    </>
  );
};

//

type NavItemCProps = NavItemProps & ListItemButtonProps;

const NavItem: React.FC<NavItemCProps> = ({ item, depth, active, open, isCollapse, ...other }) => {
  const { title, icon, children, disabled, roles } = item;

  const renderContent = (
    <ListItemStyle
      disableRipple
      depth={depth}
      active={active}
      disabled={disabled}
      id={item?.id}
      {...other}
      isCollapse={isCollapse}
    >
      {icon && (
        <ListItemIcon sx={{ width: 18, height: 18, '& svg': { width: '100%', height: '100%' } }}>
          {icon}
        </ListItemIcon>
      )}

      {/* TODO: Remove later */}
      {depth !== 1 && <Box sx={{ mx: 0.6 }} />}

      <StackRow gap={0}>
        <ListItemText primary={title} />
        {disabled && <IconLock sx={{ height: 16 }} />}
      </StackRow>

      {!isCollapse && (
        <>
          {!!children && (
            <Stack justifyContent="center" sx={{ ml: 'auto', flexShrink: 0 }}>
              {open ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
            </Stack>
          )}
        </>
      )}
    </ListItemStyle>
  );

  return <RoleBasedGuard roles={roles}>{renderContent}</RoleBasedGuard>;
};

// styles:

interface ListItemStyleProps extends ListItemButtonProps {
  active: boolean;
  depth: number;
  isCollapse?: boolean;
  disabled?: boolean;
}

const ListItemStyle = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'isCollapse' && prop !== 'disabled',
})<ListItemStyleProps>(({ active, depth, isCollapse, disabled, theme }) => ({
  position: 'relative',
  padding: theme.spacing(1),
  color: theme.palette.text.primary,
  ...(depth && depth > 1
    ? {
        marginBottom: theme.spacing(0.5),
        gap: 0,
        ...(active && {
          '& .MuiTypography-root': { color: theme.palette.primary.main },
        }),
      }
    : {
        '& .MuiTypography-root': { fontWeight: 600 },
        marginBottom: theme.spacing(0.5),
        gap: theme.spacing(1),
        ...(active && {
          '&.MuiListItemButton-root': { backgroundColor: theme.palette.primary.lighter },
        }),
      }),
  listStyle: 'none',
  '& .MuiListItemIcon-root': { margin: isCollapse ? '0 auto' : '0' },
  // Sub item
  ...(depth && {
    paddingLeft: theme.spacing(depth),
  }),
}));
